import React, { useState, useEffect } from 'react';
import callHistoryService from '../services/callHistoryService';
import { useGlobalContext } from '../../context/GlobalContext';

const MyExtensionModal = ({ show, onClose, onSaved }) => {
  const { user } = useGlobalContext();
  const [extension, setExtension] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [confirmPrompt, setConfirmPrompt] = useState(null); // { extension, pbxName }

  useEffect(() => {
    if (show && user?.emp_id) {
      const cached = localStorage.getItem(`my_extension_${user.emp_id}`);
      if (cached) setExtension(cached);
      setConfirmPrompt(null);
      setMessage({ text: '', type: '' });
      fetchCurrentExtension();
    }
  }, [show, user]);

  const fetchCurrentExtension = async () => {
    setLoading(true);
    try {
      const res = await callHistoryService.getMyExtension(user.emp_id);
      if (res && res.status === 0 && res.extension) {
        setExtension(res.extension);
        localStorage.setItem(`my_extension_${user.emp_id}`, res.extension);
      }
    } catch (err) {
      console.error("Error loading extension:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleInitialSubmit = async (e) => {
    e.preventDefault();
    if (!extension.trim()) {
      setMessage({ text: 'Please enter a valid extension number.', type: 'danger' });
      return;
    }

    setLoading(true);
    setMessage({ text: '', type: '' });
    setConfirmPrompt(null);

    try {
      const cleanExt = extension.trim();
      const res = await callHistoryService.checkMyExtension(cleanExt);
      if (res && res.status === 0 && res.pbx_name) {
        setConfirmPrompt({
          extension: cleanExt,
          pbxName: res.pbx_name
        });
      } else {
        setMessage({ text: res.error || res.message || `Extension '${cleanExt}' does not exist in PBX system.`, type: 'danger' });
      }
    } catch (err) {
      console.error("Error checking extension:", err);
      setMessage({ text: 'Error connecting to server.', type: 'danger' });
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmSave = async () => {
    if (!confirmPrompt?.extension) return;
    setLoading(true);
    setMessage({ text: '', type: '' });

    try {
      const cleanExt = confirmPrompt.extension;
      const res = await callHistoryService.saveMyExtension(
        user.emp_id,
        cleanExt,
        user.name || '',
        'save'
      );

      if (res && res.status === 0) {
        localStorage.setItem(`my_extension_${user.emp_id}`, cleanExt);
        setMessage({ text: res.message || `Extension ${cleanExt} saved successfully!`, type: 'success' });
        setConfirmPrompt(null);
        if (onSaved) onSaved(cleanExt);
        setTimeout(() => {
          setMessage({ text: '', type: '' });
          onClose();
        }, 1200);
      } else {
        setMessage({ text: res.error || res.message || 'Failed to save extension.', type: 'danger' });
      }
    } catch (err) {
      console.error("Error saving extension:", err);
      setMessage({ text: 'Error connecting to server.', type: 'danger' });
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div 
      className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1050, backgroundColor: 'rgba(0,0,0,0.5)' }}
    >
      <div className="bg-white dark:bg-[#1B1B1B] rounded-lg shadow-xl w-full max-w-md mx-4 overflow-hidden border border-gray-200 dark:border-gray-700">
        <div className="flex justify-between items-center px-5 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-[#252525]">
          <h5 className="text-base font-bold text-gray-800 dark:text-white flex items-center gap-2 mb-0">
            <i className="ri-phone-fill text-primary"></i>
            <span>My Phone Extension</span>
          </h5>
          <button 
            type="button" 
            className="text-gray-400 hover:text-gray-600 text-lg leading-none"
            onClick={onClose}
          >
            &times;
          </button>
        </div>

        <form onSubmit={handleInitialSubmit} className="p-5">
          {message.text && (
            <div className={`p-3 mb-4 text-xs rounded-md ${message.type === 'success' ? 'bg-success/10 text-success border border-success/20' : 'bg-danger/10 text-danger border border-danger/20'}`}>
              {message.text}
            </div>
          )}

          {confirmPrompt && (
            <div className="p-4 mb-4 rounded-md bg-info/10 border border-info/30 text-gray-800 dark:text-gray-200">
              <div className="flex items-start gap-2.5">
                <i className="ri-information-fill text-info text-lg mt-0.5"></i>
                <div>
                  <h6 className="font-bold text-xs text-info mb-1 uppercase tracking-wider">Confirm Extension Assignment</h6>
                  <p className="text-xs mb-0 leading-relaxed">
                    Extension <strong>{confirmPrompt.extension}</strong> is currently assigned to <strong>"{confirmPrompt.pbxName}"</strong>. Would you like to proceed with this extension?
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="mb-4">
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
              Employee ID
            </label>
            <input 
              type="text" 
              className="form-control form-control-sm bg-gray-100 dark:bg-gray-800 text-muted"
              value={user?.emp_id || ''} 
              disabled 
            />
          </div>

          <div className="mb-4">
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
              Employee Name
            </label>
            <input 
              type="text" 
              className="form-control form-control-sm bg-gray-100 dark:bg-gray-800 text-muted"
              value={user?.name || ''} 
              disabled 
            />
          </div>

          <div className="mb-4">
            <label className="block text-xs font-semibold text-gray-800 dark:text-gray-200 mb-1">
              PBX Extension Number <span className="text-danger">*</span>
            </label>
            <input 
              type="text" 
              className="form-control form-control-sm"
              placeholder="e.g. 1001"
              value={extension}
              onChange={(e) => {
                setExtension(e.target.value);
                if (confirmPrompt) setConfirmPrompt(null);
              }}
              required
            />
            <small className="text-xs text-muted block mt-1">
              Enter your assigned desk phone or softphone extension to view your call history.
            </small>
          </div>

          <div className="flex justify-end gap-2 pt-3 border-t border-gray-100 dark:border-gray-800">
            <button 
              type="button" 
              className="btn btn-sm btn-light border px-4 text-xs"
              onClick={() => {
                if (confirmPrompt) {
                  setConfirmPrompt(null);
                } else {
                  onClose();
                }
              }}
              disabled={loading}
            >
              Cancel
            </button>

            {confirmPrompt ? (
              <button 
                type="button" 
                className="btn btn-sm btn-success px-4 text-xs font-bold"
                onClick={handleConfirmSave}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Yes, Proceed & Save'}
              </button>
            ) : (
              <button 
                type="submit" 
                className="btn btn-sm btn-primary px-4 text-xs"
                disabled={loading}
              >
                {loading ? 'Checking...' : 'Save Extension'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default MyExtensionModal;
